import { Badge, Button, DatePicker, Dropdown, Input, Menu, Popover, Table } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { loadAllTransaction } from "../../redux/actions/transaction/getTransactionAction";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../utils/dateFilterCache";
import "./transaction.css";

const FILTER_STYLE = { marginBottom: 12 };
const FILTER_LABEL = { display: "block", marginBottom: 4, color: "rgba(0,0,0,0.65)", fontSize: 12 };

const columns = [
  {
    title: "S.No",
    key: "sNo",
    width: 60,
    render: (_, __, index) => index + 1,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (date) => moment(date).format("ll"),
  },
  {
    title: "Debit Account",
    dataIndex: "debit",
    key: "debit",
    render: (debit, record) => debit?.name ? <Link to={`/transaction/${record.id}`}>{debit.name}</Link> : "—",
  },
  {
    title: "Credit Account",
    dataIndex: "credit",
    key: "credit",
    render: (credit, record) => credit?.name ? <Link to={`/transaction/${record.id}`}>{credit.name}</Link> : "—",
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    responsive: ["md"],
  },
  {
    title: "Particulars",
    dataIndex: "particulars",
    key: "particulars",
  },
];

const menuItems = columns.map((item) => ({ key: item.key, label: <span>{item.title}</span> }));

function CustomTable({ list, total, startdate, enddate, columnItems, columnsToShow, setColumnsToShow, colVisibilityClickHandler, addKeys, filteredList }) {
  const dispatch = useDispatch();
  const hasCol = (key) => Array.isArray(columnsToShow) && columnsToShow.some((c) => c.key === key);
  const dataSource = filteredList ? addKeys(filteredList) : list ? addKeys(list) : [];

  return (
    <div className="list-page-table-container">
      <div className="list-page-table-inner">
        <div className="list-page-table-wrap">
          {/* Desktop table */}
          <div className="d-none d-lg-block">
            <Table
              scroll={{ x: true, y: 450 }}
              loading={!list}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showSizeChanger: true,
                total: total ?? 0,
                onChange: (page, limit) => {
                  dispatch(loadAllTransaction({ page, limit, startdate, enddate }));
                },
              }}
              columns={columnsToShow}
              dataSource={dataSource}
            />
          </div>

          {/* Mobile cards */}
          <div className="d-lg-none transaction-mobile-cards">
            {dataSource && dataSource.length > 0 ? (
              <div className="transaction-mobile-list">
                {dataSource.map((record) => (
                  <div key={record.id} className="transaction-mobile-card">
                    <div className="transaction-mobile-card-body">
                      <div className="transaction-mobile-top">
                        <div className="transaction-mobile-title">
                          <Link to={`/transaction/${record.id}`}>{record.id}</Link>
                        </div>
                      </div>

                      <div className="transaction-mobile-grid">
                        {hasCol("date") && (
                          <div className="transaction-mobile-item">
                            <span className="transaction-mobile-label">Date</span>
                            <span className="transaction-mobile-value">
                              {record.date ? moment(record.date).format("DD/MM/YYYY") : "—"}
                            </span>
                          </div>
                        )}

                        {hasCol("debit") && (
                          <div className="transaction-mobile-item">
                            <span className="transaction-mobile-label">Debit</span>
                            <span className="transaction-mobile-value">{record.debit?.name || "—"}</span>
                          </div>
                        )}

                        {hasCol("credit") && (
                          <div className="transaction-mobile-item">
                            <span className="transaction-mobile-label">Credit</span>
                            <span className="transaction-mobile-value">{record.credit?.name || "—"}</span>
                          </div>
                        )}

                        {hasCol("amount") && (
                          <div className="transaction-mobile-item">
                            <span className="transaction-mobile-label">Amount</span>
                            <span className="transaction-mobile-value">{record.amount ?? "—"}</span>
                          </div>
                        )}

                        {hasCol("particulars") && (
                          <div className="transaction-mobile-item transaction-mobile-item-wide">
                            <span className="transaction-mobile-label">Particulars</span>
                            <span className="transaction-mobile-value">{record.particulars || "—"}</span>
                          </div>
                        )}
                      </div>

                      <div className="transaction-mobile-actions">
                        <Link to={`/transaction/${record.id}`}>
                          <Button type="primary" block size="small" shape="round">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No transactions found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const GetAllTransaction = ({ refreshTrigger, addTransactionButton }) => {
  const [startdate, setStartdate] = useState(() => {
    const c = getCachedDateFilter(CACHE_KEYS.TRANSACTION_LIST);
    return c ? moment(c.startdate) : moment().startOf("month");
  });
  const [enddate, setEnddate] = useState(() => {
    const c = getCachedDateFilter(CACHE_KEYS.TRANSACTION_LIST);
    return c ? moment(c.enddate) : moment().endOf("month");
  });
  const dispatch = useDispatch();
  const list = useSelector((state) => state.transactions.list);
  const total = useSelector((state) => state.transactions.total);
  const getListLimit = () =>
    typeof window !== "undefined" && window.innerWidth < 992 ? 1000 : 10;
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({ debit: "", credit: "", particulars: "", amountMin: "", amountMax: "" });

  const { RangePicker } = DatePicker;

  useEffect(() => {
    dispatch(
      loadAllTransaction({
        page: 1,
        limit: getListLimit(),
        startdate: startdate?.format?.("YYYY-MM-DD") ?? startdate,
        enddate: enddate?.format?.("YYYY-MM-DD") ?? enddate,
      })
    );
  }, [dispatch, refreshTrigger]);

  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      setColumnsToShow(columnsToShow.filter((item) => item.key !== col.key));
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      const updated = [...columnsToShow];
      updated.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updated);
    }
  };

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  const debitLower = (filters.debit || "").trim().toLowerCase();
  const creditLower = (filters.credit || "").trim().toLowerCase();
  const particularsLower = (filters.particulars || "").trim().toLowerCase();
  const amountMin = filters.amountMin.trim() === "" ? null : parseFloat(filters.amountMin);
  const amountMax = filters.amountMax.trim() === "" ? null : parseFloat(filters.amountMax);
  const hasAmountFilter = amountMin != null && !isNaN(amountMin) || amountMax != null && !isNaN(amountMax);
  const filteredList =
    list && (debitLower || creditLower || particularsLower || hasAmountFilter)
      ? list.filter((i) => {
          if (debitLower && !(i.debit?.name || "").toLowerCase().includes(debitLower)) return false;
          if (creditLower && !(i.credit?.name || "").toLowerCase().includes(creditLower)) return false;
          if (particularsLower && !(i.particulars || "").toLowerCase().includes(particularsLower)) return false;
          const amt = Number(i.amount);
          if (amountMin != null && !isNaN(amountMin) && (isNaN(amt) || amt < amountMin)) return false;
          if (amountMax != null && !isNaN(amountMax) && (isNaN(amt) || amt > amountMax)) return false;
          return true;
        })
      : list;
  const isDefaultDateRange =
    startdate?.isSame?.(moment().startOf("month"), "day") &&
    enddate?.isSame?.(moment().endOf("month"), "day");
  const dateFilterActive = !isDefaultDateRange;
  const activeFilterCount =
    [filters.debit, filters.credit, filters.particulars].filter(Boolean).length +
    (hasAmountFilter ? 1 : 0) +
    (dateFilterActive ? 1 : 0);

  const onCalendarChange = (dates) => {
    const Newstartdate = dates?.[0];
    const Newenddate = dates?.[1];
    if (Newstartdate && Newenddate) {
      const startStr = Newstartdate.format("YYYY-MM-DD");
      const endStr = Newenddate.format("YYYY-MM-DD");
      setStartdate(Newstartdate);
      setEnddate(Newenddate);
      setCachedDateFilter(CACHE_KEYS.TRANSACTION_LIST, { startdate: startStr, enddate: endStr });
      dispatch(loadAllTransaction({ page: 1, limit: getListLimit(), startdate: startStr, enddate: endStr }));
    }
  };

  const clearAllFilters = () => {
    setFilters({ debit: "", credit: "", particulars: "", amountMin: "", amountMax: "" });
    const newStart = moment().startOf("month");
    const newEnd = moment().endOf("month");
    setStartdate(newStart);
    setEnddate(newEnd);
    setCachedDateFilter(CACHE_KEYS.TRANSACTION_LIST, { startdate: newStart, enddate: newEnd });
    dispatch(
      loadAllTransaction({
        page: 1,
        limit: getListLimit(),
        startdate: newStart.format("YYYY-MM-DD"),
        enddate: newEnd.format("YYYY-MM-DD"),
      })
    );
    setFilterVisible(false);
  };

  const filterContent = (
    <div style={{ width: 260 }}>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Debit Account</label>
        <Input
          placeholder="Filter by debit account"
          value={filters.debit}
          onChange={(e) => setFilters((f) => ({ ...f, debit: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Credit Account</label>
        <Input
          placeholder="Filter by credit account"
          value={filters.credit}
          onChange={(e) => setFilters((f) => ({ ...f, credit: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Particulars</label>
        <Input
          placeholder="Filter by particulars"
          value={filters.particulars}
          onChange={(e) => setFilters((f) => ({ ...f, particulars: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Amount (Min)</label>
        <Input
          type="number"
          placeholder="Min amount"
          value={filters.amountMin}
          onChange={(e) => setFilters((f) => ({ ...f, amountMin: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Amount (Max)</label>
        <Input
          type="number"
          placeholder="Max amount"
          value={filters.amountMax}
          onChange={(e) => setFilters((f) => ({ ...f, amountMax: e.target.value }))}
          allowClear
        />
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Button size="small" onClick={clearAllFilters}>
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div className="card card-custom list-page-fill-viewport">
      <div className="card-body">
        <div className="card-title d-flex justify-content-between align-items-center list-page-header" style={{ marginBottom: 0, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h5 className="mb-0">
              <span>Transaction History</span>
            </h5>
            <RangePicker
              value={[startdate, enddate]}
              onCalendarChange={onCalendarChange}
              size="small"
              style={{ width: 240 }}
            />
          </div>
          <div className="transaction-header-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {list && (
              <>
                <Popover
                  content={filterContent}
                  title="Filters"
                  trigger="click"
                  open={filterVisible}
                  onOpenChange={setFilterVisible}
                  placement="bottomLeft"
                >
                  <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                    <Button
                      type="primary"
                      className="filter-icon-btn"
                      icon={<FilterOutlined />}
                      onClick={() => setFilterVisible((v) => !v)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Badge>
                </Popover>
                <Dropdown
                  overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                  placement="bottomRight"
                >
                  <Button className="column-visibility">Column Visibility</Button>
                </Dropdown>
              </>
            )}
            {addTransactionButton}
          </div>
        </div>
        <CustomTable
          list={list}
          total={total?._count?.id}
          startdate={startdate}
          enddate={enddate}
          columnItems={columnItems}
          columnsToShow={columnsToShow}
          setColumnsToShow={setColumnsToShow}
          colVisibilityClickHandler={colVisibilityClickHandler}
          addKeys={addKeys}
          filteredList={filteredList}
        />
      </div>
    </div>
  );
};

export default GetAllTransaction;
